import apiClient from './api';

const USERS_BASE = '/users';

/** User metadata - custom fields stored as key-value pairs */
export type UserMetadata = Record<string, string | number | boolean | null>;

// All available user roles
export type UserRole =
  | 'admin'
  | 'user'
  | 'tenant_admin'
  | 'agent_developer'
  | 'analyst'
  | 'viewer'
  | 'partner'
  | 'superuser'
  // Legacy roles kept for backward compatibility during migration.
  | 'developer'
  | 'tenant-admin'
  | 'platform-admin'
  | 'superadmin';

export interface TenantMembership {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  role_name: string;
  granted_at: string;
  is_primary: boolean;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  tenant_id: string;
  tenant_name?: string;
  tenant_slug?: string;
  role: UserRole;
  is_active: boolean;
  /** The IAM account status (active, locked, disabled, …); 'disabled' counts as deactivated. */
  status?: string | null;
  preferred_language?: string;
  last_login?: string;
  created_at: string;
  updated_at?: string;
  metadata?: UserMetadata;
  agent_count?: number;
  api_key_count?: number;
  tenant_memberships?: TenantMembership[];
}

export interface CreateUserInput {
  email: string;
  first_name: string;
  last_name: string;
  tenant_id: string;
  role?: UserRole;
  password?: string;
}

export interface UpdateUserInput {
  email?: string;
  first_name?: string;
  last_name?: string;
  metadata?: UserMetadata;
}

export interface CreateUserResponse {
  user: User;
  message?: string;
  idp_user_id?: string;
  password_email_sent?: boolean;
}

/** Activity event details */
export type ActivityDetails = Record<string, string | number | boolean | null>;

export interface UserActivity {
  id: string;
  event_type: string;
  resource_type: string;
  resource_id: string;
  details?: ActivityDetails;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface UserActivityResponse {
  activity: UserActivity[];
  statistics: {
    agents_created: number;
    api_keys_created: number;
    documents_uploaded: number;
    last_login?: string;
  };
}

export interface ResetPasswordResponse {
  success?: boolean;
  message?: string;
  email: string;
  delivery?: 'email' | 'reset_link';
  email_sent?: boolean;
  email_error?: string;
  reset_url?: string;
  expires_at?: string;
}

export const usersService = {
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    tenant_id?: string;
    role?: string;
    is_active?: boolean;
  }): Promise<{ users: User[]; total: number; page: number; pageSize: number; totalPages: number }> {
    // Convert page to skip for backend pagination (backend expects skip, not page)
    const { page, limit = 20, ...restParams } = params || {};
    const skip = page ? (page - 1) * limit : 0;

    const response = await apiClient.get(USERS_BASE, {
      params: {
        skip,
        limit,
        ...restParams
      }
    });
    return response.data;
  },

  async getById(id: string): Promise<User> {
    const response = await apiClient.get(`${USERS_BASE}/${id}`);
    return response.data?.user;
  },

  async create(data: CreateUserInput): Promise<CreateUserResponse> {
    const response = await apiClient.post(USERS_BASE, data);
    return response.data;
  },

  async update(id: string, data: UpdateUserInput): Promise<User> {
    const response = await apiClient.put(`${USERS_BASE}/${id}`, data);
    return response.data?.user;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${USERS_BASE}/${id}`);
  },

  async updateRole(id: string, role: string): Promise<User> {
    const primaryPath = `${USERS_BASE}/${id}/role`;
    const fallbackPath = `/admin/users/${id}/role`;
    try {
      const response = await apiClient.patch(primaryPath, { role });
      return response.data?.user ?? response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 405) {
        const response = await apiClient.patch(fallbackPath, { role });
        return response.data?.user ?? response.data;
      }
      throw error;
    }
  },

  async updateStatus(id: string, is_active: boolean): Promise<User> {
    const response = await apiClient.patch(`${USERS_BASE}/${id}/status`, { is_active });
    return response.data?.user;
  },

  async resetPassword(id: string, temporary = true): Promise<ResetPasswordResponse> {
    const response = await apiClient.post(`${USERS_BASE}/${id}/reset-password`, { temporary });
    return response.data;
  },

  /** Removes another account's second factor and ends its sign-ins (iam-service routes/mfa_admin.py). */
  async resetMfa(id: string): Promise<{ status?: string }> {
    const response = await apiClient.post(`/mfa/admin/users/${encodeURIComponent(id)}/reset`);
    return response.data;
  },

  async getActivity(id: string, limit = 50): Promise<UserActivityResponse> {
    const response = await apiClient.get(`${USERS_BASE}/${id}/activity`, { params: { limit } });
    return response.data;
  },

  async updateLanguage(id: string, preferred_language: string): Promise<User> {
    const primaryPath = `${USERS_BASE}/${id}/language`;
    const fallbackPath = `/admin/users/${id}/language`;
    try {
      const response = await apiClient.patch(primaryPath, { preferred_language });
      return response.data?.user ?? response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 405) {
        const response = await apiClient.patch(fallbackPath, { preferred_language });
        return response.data?.user ?? response.data;
      }
      throw error;
    }
  },

  /**
   * Track user login - updates last_login timestamp in database.
   * Should be called once per authenticated browser session.
   */
  async trackLogin(options?: { clientApp?: string }): Promise<{ status: string; user_id: string; tracked_at: string }> {
    const response = await apiClient.post('/users/track-login', undefined, {
      headers: options?.clientApp ? { 'X-Client-App': options.clientApp } : undefined,
    });
    return response.data;
  },

  async getTenantMemberships(userId: string): Promise<TenantMembership[]> {
    const response = await apiClient.get(`${USERS_BASE}/${userId}/tenants`);
    return response.data?.memberships ?? [];
  },

  async addTenantMembership(userId: string, tenantId: string, role = 'user'): Promise<void> {
    await apiClient.post(`${USERS_BASE}/${userId}/tenants/${tenantId}`, { role });
  },

  async removeTenantMembership(userId: string, tenantId: string): Promise<void> {
    await apiClient.delete(`${USERS_BASE}/${userId}/tenants/${tenantId}`);
  },

  async moveTenantMembership(
    userId: string,
    fromTenantId: string,
    toTenantId: string,
    role = 'user',
  ): Promise<void> {
    await apiClient.post(`${USERS_BASE}/${userId}/move`, {
      from_tenant_id: fromTenantId,
      to_tenant_id: toTenantId,
      role,
    });
  },
};

// ---------------------------------------------------------------------------
// Invitation types & service
// ---------------------------------------------------------------------------

export interface Invitation {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  tenant_name: string;
  invited_by_name: string;
  expires_at: string | null;
  accepted_at: string | null;
  created_at: string | null;
}

export interface CreateInvitationInput {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  tenant_id: string;
  message?: string;
  trial_period_months?: number;
}

export const invitationsService = {
  async list(params?: {
    tenant_id?: string;
    status_filter?: string;
  }): Promise<{ invitations: Invitation[]; total: number }> {
    const response = await apiClient.get('/invitations', { params });
    return response.data;
  },

  async create(data: CreateInvitationInput): Promise<Invitation> {
    const response = await apiClient.post('/invitations', data);
    return response.data;
  },

  async resend(id: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`/invitations/${id}/resend`);
    return response.data;
  },

  async revoke(id: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete(`/invitations/${id}`);
    return response.data;
  },
};
